const AUTH_RETURN_KEY = "cshaped_pending_auth_return_v1";
const CHECKOUT_RETURN_KEY = "cshaped_pending_checkout_return_v1";
const RETURN_TTL_MS = 24 * 60 * 60 * 1000;

type StoredReturn = {
  path: string;
  expires_at: number;
};

type StoredCheckoutReturn = StoredReturn & {
  invoice_id: string;
};

export function safeInternalPath(value?: string | null): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;

  try {
    const origin = window.location.origin;
    const parsed = new URL(value, origin);
    if (parsed.origin !== origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function readStoredReturn(key: string): StoredReturn | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredReturn;
    const path = safeInternalPath(parsed.path);
    if (!path || !Number.isFinite(parsed.expires_at) || parsed.expires_at <= Date.now()) {
      window.localStorage.removeItem(key);
      return null;
    }
    return { path, expires_at: parsed.expires_at };
  } catch {
    return null;
  }
}

export function rememberAuthReturn(path: string): string | null {
  const safePath = safeInternalPath(path);
  if (!safePath) return null;
  try {
    window.localStorage.setItem(AUTH_RETURN_KEY, JSON.stringify({
      path: safePath,
      expires_at: Date.now() + RETURN_TTL_MS,
    }));
  } catch { }
  return safePath;
}

export function peekAuthReturn(): string | null {
  return readStoredReturn(AUTH_RETURN_KEY)?.path || null;
}

export function consumeAuthReturn(): string | null {
  const path = peekAuthReturn();
  try { window.localStorage.removeItem(AUTH_RETURN_KEY); } catch { }
  return path;
}

const SIGNUP_RETURN_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const SIGNUP_UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id"] as const;

export type SignupUtm = Partial<Record<typeof SIGNUP_UTM_KEYS[number], string>>;

export type SignupJourneyMetadata = {
  signup_return_to?: string;
  signup_landing_path?: string;
  signup_utm?: SignupUtm;
  signup_coupon?: string;
};

type SignupJourneySource = {
  returnTo?: string | null;
  attribution?: Partial<Record<string, unknown>> | null;
  coupon?: string | null;
};

/**
 * Email confirmation links often open in a different browser than the one
 * used to sign up (for example, a signup inside TikTok's in-app browser is
 * confirmed in Safari). Store the pending destination, campaign UTMs and
 * coupon on the new account so the journey survives that switch. Click IDs
 * are deliberately excluded: they are consent-gated and erased on withdrawal,
 * which account metadata would not honour.
 */
export function buildSignupJourneyMetadata({ returnTo, attribution, coupon }: SignupJourneySource): SignupJourneyMetadata {
  const metadata: SignupJourneyMetadata = {};
  const safeReturn = safeInternalPath(returnTo);
  if (safeReturn) metadata.signup_return_to = safeReturn;

  const utm: SignupUtm = {};
  for (const key of SIGNUP_UTM_KEYS) {
    const value = attribution?.[key];
    if (typeof value === "string" && value.trim()) utm[key] = value.trim().slice(0, 256);
  }
  if (Object.keys(utm).length) {
    metadata.signup_utm = utm;
    try {
      const landing = new URL(String(attribution?.landing_page_url || ""));
      if (landing.origin === window.location.origin) metadata.signup_landing_path = landing.pathname;
    } catch { }
  }

  const code = coupon?.trim().toUpperCase();
  if (code && /^[A-Z0-9_-]{1,40}$/.test(code)) metadata.signup_coupon = code;
  return metadata;
}

/** The stored destination, only while the account is new enough to still be confirming. */
export function signupReturnFromMetadata(metadata: unknown, createdAt?: string | null, now = Date.now()): string | null {
  const path = safeInternalPath((metadata as SignupJourneyMetadata | null)?.signup_return_to);
  const created = createdAt ? Date.parse(createdAt) : NaN;
  if (!path || !Number.isFinite(created) || now - created > SIGNUP_RETURN_MAX_AGE_MS) return null;
  return path;
}

export function signupUtmFromMetadata(metadata: unknown, createdAt?: string | null, now = Date.now()): SignupUtm | null {
  const created = createdAt ? Date.parse(createdAt) : NaN;
  if (!Number.isFinite(created) || now - created > SIGNUP_RETURN_MAX_AGE_MS) return null;
  const stored = (metadata as SignupJourneyMetadata | null)?.signup_utm;
  const utm: SignupUtm = {};
  for (const key of SIGNUP_UTM_KEYS) {
    const value = stored?.[key];
    if (typeof value === "string" && value) utm[key] = value.slice(0, 256);
  }
  return utm.utm_source ? utm : null;
}

export function rememberCheckoutReturn(invoiceId: string, path?: string | null): string | null {
  const safePath = safeInternalPath(path);
  if (!invoiceId || !safePath) return null;
  try {
    window.localStorage.setItem(CHECKOUT_RETURN_KEY, JSON.stringify({
      invoice_id: invoiceId,
      path: safePath,
      expires_at: Date.now() + RETURN_TTL_MS,
    } satisfies StoredCheckoutReturn));
  } catch { }
  return safePath;
}

export function consumeCheckoutReturn(invoiceId?: string | null): string | null {
  try {
    const raw = window.localStorage.getItem(CHECKOUT_RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredCheckoutReturn;
    const path = safeInternalPath(parsed.path);
    if (
      !path
      || !invoiceId
      || parsed.invoice_id !== invoiceId
      || !Number.isFinite(parsed.expires_at)
      || parsed.expires_at <= Date.now()
    ) {
      if (!Number.isFinite(parsed.expires_at) || parsed.expires_at <= Date.now()) {
        window.localStorage.removeItem(CHECKOUT_RETURN_KEY);
      }
      return null;
    }
    window.localStorage.removeItem(CHECKOUT_RETURN_KEY);
    return path;
  } catch {
    return null;
  }
}
